import React from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import type { PharmacySale } from '../../types/pharmacy-pos';

interface PrintTemplate80mmProps {
  sale: PharmacySale;
  hospitalName?: string;
  hospitalAddress?: string;
  hospitalPhone?: string;
}

export const PrintTemplate80mm: React.FC<PrintTemplate80mmProps> = ({
  sale,
  hospitalName = 'Medical Center',
  hospitalAddress = 'Hospital Address',
  hospitalPhone = 'Phone: +880-XXX-XXXXXX'
}) => {
  const { formatCurrency } = useCurrency();
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="print-template-80mm" style={{ width: '80mm', fontFamily: 'monospace', fontSize: '12px' }}>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-template-80mm, .print-template-80mm * {
            visibility: visible;
          }
          .print-template-80mm {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            padding: 5mm;
          }
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '10px', borderBottom: '1px dashed #000', paddingBottom: '10px' }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>{hospitalName}</div>
        <div style={{ fontSize: '10px', marginBottom: '2px' }}>{hospitalAddress}</div>
        <div style={{ fontSize: '10px', marginBottom: '5px' }}>{hospitalPhone}</div>
        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>PHARMACY INVOICE</div>
      </div>

      {/* Invoice Details */}
      <div style={{ marginBottom: '10px', fontSize: '11px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
          <span>Invoice No:</span>
          <span style={{ fontWeight: 'bold' }}>{sale.invoice_no}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
          <span>Date:</span>
          <span>{formatDate(sale.created_at)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
          <span>Type:</span>
          <span style={{ textTransform: 'uppercase' }}>{sale.sale_type}</span>
        </div>
      </div>

      {/* Patient Info */}
      {(sale.patient_name || sale.patient_uhid) && (
        <div style={{ marginBottom: '10px', fontSize: '11px', borderTop: '1px dashed #000', paddingTop: '5px' }}>
          {sale.patient_uhid && (
            <div style={{ marginBottom: '2px' }}>
              <strong>UHID:</strong> {sale.patient_uhid}
            </div>
          )}
          {sale.patient_name && (
            <div style={{ marginBottom: '2px' }}>
              <strong>Patient:</strong> {sale.patient_name}
            </div>
          )}
          {sale.patient_phone && (
            <div style={{ marginBottom: '2px' }}>
              <strong>Phone:</strong> {sale.patient_phone}
            </div>
          )}
        </div>
      )}

      {/* Items Table */}
      <div style={{ marginBottom: '10px', borderTop: '1px dashed #000', paddingTop: '5px' }}>
        <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #000' }}>
              <th style={{ textAlign: 'left', padding: '3px 0' }}>Item</th>
              <th style={{ textAlign: 'center', padding: '3px 0' }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '3px 0' }}>Price</th>
              <th style={{ textAlign: 'right', padding: '3px 0' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {sale.items?.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px dotted #ccc' }}>
                <td style={{ padding: '3px 0', fontSize: '9px' }}>
                  <div>{item.medicine_name}</div>
                  {item.generic_name && (
                    <div style={{ fontSize: '8px', color: '#666' }}>({item.generic_name})</div>
                  )}
                </td>
                <td style={{ textAlign: 'center', padding: '3px 0' }}>{item.quantity}</td>
                <td style={{ textAlign: 'right', padding: '3px 0' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ textAlign: 'right', padding: '3px 0' }}>{formatCurrency(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ marginBottom: '10px', fontSize: '11px', borderTop: '1px dashed #000', paddingTop: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
          <span>Subtotal:</span>
          <span>{formatCurrency(sale.subtotal)}</span>
        </div>
        {sale.discount_amount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
            <span>Discount:</span>
            <span>-{formatCurrency(sale.discount_amount)}</span>
          </div>
        )}
        {sale.vat_amount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
            <span>VAT ({sale.vat_percent}%):</span>
            <span>{formatCurrency(sale.vat_amount)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #000', fontWeight: 'bold', fontSize: '13px' }}>
          <span>GRAND TOTAL:</span>
          <span>{formatCurrency(sale.grand_total)}</span>
        </div>
      </div>

      {/* Payment Info */}
      {sale.payment_method && (
        <div style={{ marginBottom: '10px', fontSize: '11px', borderTop: '1px dashed #000', paddingTop: '5px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
            <span>Payment Mode:</span>
            <span style={{ textTransform: 'uppercase' }}>{sale.payment_method}</span>
          </div>
          {sale.amount_received > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                <span>Amount Received:</span>
                <span>{formatCurrency(sale.amount_received)}</span>
              </div>
              {sale.change_due > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>Change Due:</span>
                  <span>{formatCurrency(sale.change_due)}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '15px', fontSize: '10px', borderTop: '1px dashed #000', paddingTop: '10px' }}>
        <div style={{ marginBottom: '5px' }}>Thank you for your visit!</div>
        <div style={{ fontSize: '9px', color: '#666' }}>Please check medicines before leaving</div>
        <div style={{ fontSize: '9px', color: '#666', marginTop: '5px' }}>
          Printed on: {new Date().toLocaleString('en-GB')}
        </div>
      </div>
    </div>
  );
};
